import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import { Button, Classification, Quote } from '~elements'
import styles from './EDPFullScreenVideo.module.scss'
import { ButtonProps, ClassificationProps, ResponsiveMultimedia, RichTextType } from '~types'
import { useWindowSize } from 'utils/hooks'
import { getPortalRoot } from 'utils'
import Video from 'components/elements/Video/Video'

// Interfaces (consider moving to shared location)
interface QuoteProps {
  quoteTitle: string
  quoteDescription: RichTextType
  quoteSubHeadline: string
  quoteCopy: RichTextType
}

interface HygraphQuoteProps extends Partial<QuoteProps>, Partial<ClassificationProps> {}

interface EDPFullScreenVideoProps {
  alignCtaBottom?: boolean
  backgroundVideo?: ResponsiveMultimedia
  ctaPrimary?: ButtonProps
  quote?: HygraphQuoteProps[]
}

const EDPFullScreenVideo = ({ alignCtaBottom = true, backgroundVideo, ctaPrimary, quote }: EDPFullScreenVideoProps) => {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => setIsMounted(true), [])

  const { isBreakpoint: isAboveSm } = useWindowSize('sm', '>')

  const {
    backgroundVideoXl,
    backgroundVideoSm,
    videoLoop,
    posterImageXl,
    posterImageSm,
    vimeoIdXl,
    vimeoIdSm,
    allowPause,
    allowVolume
  } = backgroundVideo ?? {}

  const [wrappedQuote] = quote ?? []
  const quoteObject = wrappedQuote?.quoteTitle ? (wrappedQuote as QuoteProps) : null
  const classification = wrappedQuote?.title ? (wrappedQuote as ClassificationProps) : null
  const portalRoot = getPortalRoot()

  const CtaButton = () => {
    if (!ctaPrimary || !isMounted) return null

    const button = (
      <div className={styles['cta-container']}>
        <Button
          {...ctaPrimary}
          isFullWidth={!isAboveSm && alignCtaBottom}
          analytics={{
            linkModule: 'edp_full_screen_video'
          }}
        />
      </div>
    )

    // Button needs to sit fixed outside of document flow on mobile so we use createPortal
    // Added global .portal-fixed-cta-container class as css module does not work
    return !isAboveSm && portalRoot && alignCtaBottom
      ? ReactDOM.createPortal(<div className='portal-fixed-cta-container'>{button}</div>, portalRoot)
      : button
  }

  return (
    <div className={styles['edp-full-screen-video-container']}>
      <div
        className={cx(styles['edp-full-screen-video'], {
          [styles['align-cta-bottom']]: alignCtaBottom
        })}
      >
        <div className={styles['copy-cta-container']}>
          <div className={styles['copy-container']}>
            {quoteObject && <Quote classification={quoteObject} />}
            {classification && (
              <Classification
                title={classification?.title}
                description={classification?.description}
                ratingNumber={classification?.ratingNumber}
                ratingImage={classification?.ratingImage}
              />
            )}
            <CtaButton />
          </div>
        </div>

        <div className={styles['video-container']}>
          <Video
            videoLoop={Boolean(videoLoop)}
            backgroundVideoXl={backgroundVideoXl}
            backgroundVideoSm={backgroundVideoSm}
            posterImageXl={posterImageXl}
            posterImageSm={posterImageSm}
            vimeoIdXl={vimeoIdXl}
            vimeoIdSm={vimeoIdSm}
            allowPause={allowPause}
            allowVolume={allowVolume}
          />
        </div>
      </div>
    </div>
  )
}

export default EDPFullScreenVideo
